import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PartnerListCard from '../PartnerListCard/PartnerListCard';
import { fetchPartners } from '../../services/partners';
import './PartnerDirectory.css';

const VALUE_POINTS = [
  {
    title: 'Designed in software',
    body: 'Every Construx backyard unit starts as a digital model — engineered, documented, and ready for the field.',
  },
  {
    title: 'Built as a system',
    body: 'Precision volumetric framing ships to site and assembles fast — square, flush, and weather-tight in weeks.',
  },
  {
    title: 'Your builder is local',
    body: 'Licensed general contractors in your market handle permitting, finish-out, and the relationship on the ground.',
  },
];

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners()
      .then(setPartners)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="partner-directory font-bai bg-warm-50">
      <Helmet>
        <title>Partners — Construx</title>
        <meta
          name="description"
          content="Construx designs the system. Local build partners deliver it. Find a licensed Construx builder near you."
        />
      </Helmet>

      <section className="partner-directory-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="partner-directory-kicker mb-4">Build network</p>
          <h1 className="partner-directory-title mb-5">
            Designed by Construx. <em>Built by your local builder.</em>
          </h1>
          <p className="partner-directory-lede">
            Construx is the system — software-designed,             factory-precision framing, delivered as a
            system. Your build partner is local: a licensed GC who knows your market, your
            permits, and your backyard.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="partner-value-grid">
          {VALUE_POINTS.map((item) => (
            <div key={item.title} className="partner-value-item">
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-8 hidden" aria-hidden="true">
        <div id="partner-map-root" className="partner-map-slot">
          <div>
            <p className="partner-map-slot-label">Builder map</p>
            <p className="partner-map-slot-note">
              State map and regional filtering will mount here. For now, browse active build partners below.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="mb-8">
          <p className="text-sm font-medium uppercase tracking-widest text-construx-red mb-2">
            Active builders
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-warm-900">
            Find a Construx build partner
          </h2>
          <p className="text-warm-700 mt-3 max-w-2xl">
            Select a builder to view their profile, credentials, and field testimony.
          </p>
        </div>

        {loading ? (
          <p className="text-warm-600 text-sm">Loading builders…</p>
        ) : partners.length ? (
          <div className="partner-list-grid">
            {partners.map((partner) => (
              <PartnerListCard key={partner.id} partner={partner} />
            ))}
          </div>
        ) : (
          <p className="text-warm-600">No active build partners yet. Check back soon.</p>
        )}
      </section>

      <section className="bg-white border-t border-warm-200 py-14 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-bold text-warm-900 mb-3">Are you a builder?</h2>
          <p className="text-warm-700 mb-6">
            We&apos;re growing our builder network across Central Texas and beyond. If you&apos;re a GC
            interested in faster, stronger, precision-built ADUs, we&apos;d like to hear from you.
          </p>
          <Link
            to="/interest"
            className="inline-block px-6 py-3 bg-construx-red text-white rounded-[3px] font-medium transition-all duration-300 hover:bg-construx-red-dark hover:shadow-btn"
          >
            Come build with us
          </Link>
        </div>
      </section>
    </div>
  );
}
